import React, { useState } from 'react';

/**
 * Mid-article email capture for blog posts.
 *
 * Email only, deliberately. The existing lead-magnet popup asks for name, email
 * and phone and converts 0.41% — dropping to a single field is the biggest
 * lever available here. The name can be picked up later in chat or from a reply.
 *
 * Placement matters as much as the copy: this belongs immediately after the
 * section that answers the reader's fear, while they are relieved and paying
 * attention — not stranded at the bottom of the page.
 *
 * Props:
 *   source — post slug, so we learn which of 39 posts actually produce leads
 *   offer  — 'guidelines' (ALS-fear posts) | 'protocol' (everything else)
 */

const API_BASE = 'https://benignfasciculationsyndrome.org';

const COPY = {
  guidelines: {
    heading: 'Still not sure what you’re dealing with?',
    body:
      'Our BFS Guidelines walk through how benign twitching is actually told apart from the conditions people worry about — in plain English, including what doctors look for.',
    button: 'Send me the guidelines',
  },
  protocol: {
    heading: 'Want something practical to try?',
    body:
      'The 14-Day BFS Calm Protocol is a simple day-by-day plan covering sleep, hydration and stress — the things that most often settle twitching down.',
    button: 'Send me the protocol',
  },
};

export default function InlineOptIn({ source, offer = 'protocol' }) {
  const copy = COPY[offer] || COPY.protocol;
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | done | error
  const [error, setError] = useState('');

  const valid = e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  async function handleSubmit(e) {
    e.preventDefault();
    const clean = email.trim();
    if (!valid(clean)) {
      setError('That email doesn’t look right — mind checking it?');
      return;
    }
    setStatus('sending');
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/leads/inline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: clean, source, offer }),
      });
      if (!res.ok) throw new Error('request failed');
      setStatus('done');
    } catch (err) {
      setStatus('error');
      setError('Something went wrong on our end. Please try again in a moment.');
    }
  }

  if (status === 'done') {
    return (
      <aside style={{ ...styles.box, ...styles.doneBox }} aria-live="polite">
        <div style={styles.heading}>
          Check your inbox{' '}
          <span role="img" aria-label="incoming mail">📬</span>
        </div>
        <p style={styles.body}>
          It’s on its way to <strong>{email.trim()}</strong>. If it isn’t there in a few minutes,
          have a look in spam — it arrives as an attachment, which some providers tuck away.
        </p>
      </aside>
    );
  }

  return (
    <aside style={styles.box}>
      <div style={styles.heading}>{copy.heading}</div>
      <p style={styles.body}>{copy.body}</p>

      <form onSubmit={handleSubmit} style={styles.form} noValidate>
        <label htmlFor={`inline-optin-${source}`} style={styles.srOnly}>
          Your email address
        </label>
        <input
          id={`inline-optin-${source}`}
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
          disabled={status === 'sending'}
          style={styles.input}
        />
        <button type="submit" disabled={status === 'sending'} style={styles.button}>
          {status === 'sending' ? 'Sending…' : copy.button}
        </button>
      </form>

      {error && <div style={styles.error}>{error}</div>}
      <div style={styles.fine}>Free. No spam — unsubscribe any time.</div>
    </aside>
  );
}

const styles = {
  box: {
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderLeft: '4px solid #003f5c',
    borderRadius: 8,
    padding: '20px 22px',
    margin: '32px 0',
  },
  doneBox: { borderLeftColor: '#3b82f6' },
  heading: {
    fontSize: '1.15rem',
    fontWeight: 700,
    color: '#003f5c',
    marginBottom: 6,
  },
  body: { margin: '0 0 14px', color: '#374151', lineHeight: 1.55 },
  form: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  input: {
    flex: '1 1 220px',
    minWidth: 0,
    padding: '11px 13px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: '1rem',
  },
  button: {
    flex: '0 0 auto',
    padding: '11px 20px',
    background: '#003f5c',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  error: { marginTop: 10, color: '#b91c1c', fontSize: '0.9rem' },
  fine: { marginTop: 10, color: '#9ca3af', fontSize: '0.8rem' },
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0,0,0,0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
};
